import type { IChatMessage } from '@/apiClient/types/chat'
import {
  clipTrailingAfterHtmlClose,
  extractAllHtmlPreviewBlocks,
} from '@/store/preview'

/**
 * Matches a raw HTML document at the START of a reply (assistant replies that are
 * the HTML itself, not fenced). Anchored so prose merely mentioning `<html>`
 * isn't misread as a page.
 */
const HTML_DOCUMENT_RE = /^(?:<!doctype\s+html|<html[\s>])/i

/** One generated HTML page inside a message, keyed by its `blockIndex`. */
export interface ChatHtmlBlock {
  raw: string
  blockIndex: number
}

/**
 * HTML blocks in one render context (message content or a pipeline step outcome).
 * Prefers ```html``` fences (indexed 0..); if there are none but the whole context
 * is a raw HTML document, treats it as a single block at index 0 — mirrors how the
 * chat renders/publishes a fence-less HTML reply.
 */
export function htmlBlocksInContext(context: string): ChatHtmlBlock[] {
  // Handles both closed and unclosed ```html``` fences (the page is often persisted
  // as an open fence with no closing ```). `extractAllHtmlPreviewBlocks` splits on the
  // same boundaries as the backend and already clips trailing prose after `</html>`,
  // so these blocks match the previewed `content.raw` and what the API publishes.
  const fenced = extractAllHtmlPreviewBlocks(context)
  if (fenced.length) return fenced
  const trimmed = clipTrailingAfterHtmlClose(context.trim())
  if (trimmed && HTML_DOCUMENT_RE.test(trimmed)) {
    return [{ raw: trimmed, blockIndex: 0 }]
  }
  return []
}

/**
 * Every generated HTML page in one message, in document order. HTML is generated
 * either directly in the message content or inside a pipeline step's outcome — the
 * sectioned message renders each of those as its own inline card, so both are
 * scanned. Each render context indexes its ```html``` fences from 0, matching the
 * `blockIndex` the card / `htmlPublishKey` assigns; the same HTML surfacing in more
 * than one context (e.g. echoed in content and a step outcome) is returned once.
 */
export function htmlBlocksInMessage(
  message: Pick<IChatMessage, 'content' | 'ui_hints'> | null | undefined,
): ChatHtmlBlock[] {
  if (!message) return []

  const contexts: string[] = [message.content ?? '']
  for (const step of message.ui_hints?.processing_steps ?? []) {
    const outcome = step?.data?.outcome
    if (typeof outcome === 'string' && outcome) contexts.push(outcome)
  }

  const blocks: ChatHtmlBlock[] = []
  const seen = new Set<string>()
  for (const context of contexts) {
    for (const fence of htmlBlocksInContext(context)) {
      if (seen.has(fence.raw)) continue
      seen.add(fence.raw)
      blocks.push(fence)
    }
  }
  return blocks
}
